import { useSettingsNavItems } from '@oc/pages/settings/useSettingsNavItems';
import { useHeaderIntl } from 'common/header/translations/useHeaderIntl';
import { useSettingsItemPermissions } from 'common/SecondaryNavigation/useSecondaryNavigationPermissions';
import { withNestedLabelAndPath } from 'common/SecondaryNavigation/utils/getNavigationItemPath';
import React from 'react';
import { Icon } from '@beeline.com/bp-design-system';
import {
  GetSuggestedPersonsQuery,
  PersonSuggestionFragment
} from 'common/header/GlobalSearch/data/__generated__/getSuggestedPersons.graphql';
import { SuggestionOption } from 'common/header/GlobalSearch/types';
import { filterNotEmpty } from 'utils/data/listHelpers';
import { MixpanelEvents } from 'integrations/mixpanel/Events/MixpanelEvents';
import {
  GetViewHistoryQuery,
  RecentlyViewedPersonFragment
} from 'common/header/GlobalSearch/data/__generated__/getViewHistory.graphql';
import { isObject } from 'utils/guards';
import {
  SecondaryNavGroup,
  SecondaryNavItem
} from 'src/components/common/SecondaryNavigation/SecondaryNavigation.types';
import cloneDeep from 'lodash-es/cloneDeep';

export const suggestionOption = {
  toWorkerHubSearchOption(search: string, label: string): SuggestionOption {
    return {
      value: {
        workerHubSearch: search,
        event: MixpanelEvents.general.APP_HEADER_SEARCH_OPEN_HUB_SEARCH
      },
      label,
      icon: <Icon name="search" />
    };
  },
  toPersonSuggestionOption(person: PersonSuggestionFragment & { personUid: string }): SuggestionOption {
    return {
      label: `${person.fullName ?? ''} ${wrapInBracketsIfDefined(person.companyWorkerId)}`,
      subLabel: person.primaryOrgName ? [person.primaryOrgName] : undefined,
      value: {
        personUid: person.personUid,
        event: MixpanelEvents.general.APP_HEADER_SEARCH_RESULT_CLICK
      },
      icon: <Icon name="person" />
    };
  },
  toPersonSuggestionOptions(suggestions: GetSuggestedPersonsQuery['suggest']['suggestions']): SuggestionOption[] {
    return filterNotEmpty(suggestions).filter(isPersonSuggestion).map(suggestionOption.toPersonSuggestionOption);
  },
  toRecentlyViewSuggestionOption(
    person: RecentlyViewedPersonFragment & { personUid: string },
    groupName: string
  ): SuggestionOption {
    return {
      value: {
        personUid: person.personUid,
        event: MixpanelEvents.general.APP_HEADER_SEARCH_RECENTLY_VIEWED_CLICK
      },
      label: `${person.fullName ?? ''} ${wrapInBracketsIfDefined(person.companyWorkerId?.value)}`,
      groupName,
      icon: <Icon name="person" />
    };
  },
  toRecentlyViewSuggestionOptions(
    people: GetViewHistoryQuery['getViewHistory']['people'],
    groupName: string
  ): SuggestionOption[] {
    return filterNotEmpty(people)
      .filter(isRecentlyViewedPerson)
      .map((person) => suggestionOption.toRecentlyViewSuggestionOption(person, groupName));
  },
  toPagesSearchOption(groupName: string, pages: SecondaryNavItem[]): SuggestionOption[] {
    return pages.map((page) => suggestionOption.toPageSearchOption(groupName, page));
  },
  toPageSearchOption(groupName: string, page: SecondaryNavItem): SuggestionOption {
    return {
      label: page.label,
      groupName,
      value: {
        url: `settings/${page.path}`,
        event: MixpanelEvents.general.APP_HEADER_SEARCH_OPEN_PAGE_SEARCH
      },
      icon: <Icon name="settings" />
    };
  }
};

const wrapInBracketsIfDefined = (value?: string | null): string => (value ? `(${value})` : '');

export function isRecentlyViewedPerson(value: unknown): value is RecentlyViewedPersonFragment & { personUid: string } {
  return isObject(value) && 'personUid' in value;
}

export function isPersonSuggestion(value: unknown): value is PersonSuggestionFragment & { personUid: string } {
  return isObject(value) && 'personUid' in value;
}

export function usePageSuggestionOptions() {
  const permissionFilter = useSettingsItemPermissions();
  const intlHeaders = useHeaderIntl();
  const settingPages = toFlatRoutesForGlobalSearch(useSettingsNavItems()).filter(permissionFilter);
  const options = suggestionOption.toPagesSearchOption(intlHeaders.searchInPages, settingPages);
  return (search: string) => {
    return options.filter((option) => option.label.toLocaleLowerCase().includes(search?.toLocaleLowerCase()));
  };
}

export function toFlatRoutesForGlobalSearch(routes: (SecondaryNavItem | SecondaryNavGroup)[]): SecondaryNavItem[] {
  const copy = cloneDeep(routes);
  const target: SecondaryNavItem[] = [];

  while (!!copy.length) {
    const currentRoute = copy.shift();
    if (!currentRoute) {
      continue;
    }
    if ('path' in currentRoute) {
      if (!currentRoute.excludeFromGlobalSearch) {
        target.push({ ...currentRoute });
      }
      const searchableItems = currentRoute.searchableSubItems;
      if (searchableItems?.length) {
        target.push(
          ...searchableItems.map((it) =>
            withNestedLabelAndPath({ groupPath: currentRoute.path, label: currentRoute.label }, it)
          )
        );
      }
    } else {
      copy.unshift(
        ...currentRoute.items.map((subroute) => {
          return withNestedLabelAndPath(currentRoute, subroute);
        })
      );
    }
  }
  return target;
}
